import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import DataTable from "../../../components/shared/DataTable";
import Modal from "../../../components/shared/Modal";
import api from "../../../utils/api";

const EMPTY_TEST = { name: "", categoryId: "", description: "", price: "", turnaroundHours: "" };

export default function TestCatalogue() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TEST);

  const { data, isLoading } = useQuery("lab-tests", () =>
    api.get("/lab/tests")
  );

  const { data: categories } = useQuery("test-categories-list", () =>
    api.get("/lab/test-categories")
  );

  const saveMutation = useMutation(
    () =>
      editing
        ? api.put(`/lab/tests/${editing.id}`, form)
        : api.post("/lab/tests", form),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("lab-tests");
        toast.success("Saved");
        setShowModal(false);
        setEditing(null);
        setForm(EMPTY_TEST);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/lab/tests/${id}`),
    { onSuccess: () => queryClient.invalidateQueries("lab-tests") }
  );

  const columns = [
    { key: "name", label: "Test Name", sortable: true },
    { key: "category_name", label: "Category" },
    { key: "description", label: "Description" },
    {
      key: "price",
      label: "Price",
      render: (row) => `₹${row.price}`,
      sortable: true,
    },
    { key: "turnaround_hours", label: "Turnaround" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => { setEditing(row); setForm(row); setShowModal(true); }}
            className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Edit
          </button>
          <button
            onClick={() => deleteMutation.mutate(row.id)}
            className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => { setEditing(null); setForm(EMPTY_TEST); setShowModal(true); }}
          className="btn-primary"
        >
          + Add Test
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data?.tests || []}
        loading={isLoading}
        emptyMessage="No tests in catalogue."
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Test" : "Add Test to Catalogue"}
        footer={
          <>
            <button onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isLoading} className="btn-primary">
              {saveMutation.isLoading ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Category</label>
            <select
              value={form.categoryId}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              className="input-field"
            >
              <option value="">Select category</option>
              {(categories?.categories || []).map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.category_name} — {cat.test_type}</option>
              ))}
            </select>
          </div>
          {[
            { name: "name", label: "Test Name" },
            { name: "description", label: "Description" },
            { name: "price", label: "Price (₹)", type: "number" },
            { name: "turnaroundHours", label: "Turnaround (hours)", type: "number" },
          ].map(({ name, label, type = "text" }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{label}</label>
              <input
                type={type}
                value={form[name]}
                onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                className="input-field"
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
